// File: src/components/home/HomeRadioPlayer.js
import React, { useState, useEffect, useRef, useLayoutEffect } from "react";
import { getFirestore, doc, getDoc, onSnapshot } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { useMusic } from "../../context/MusicContext";
import { useProfile } from "../../context/ProfileContext";
import { motion, AnimatePresence } from "framer-motion";
import { FaVolumeMute, FaVolumeUp } from "react-icons/fa";
import useSongActions from "../../hooks/useSongActions";
import toast from "react-hot-toast";

// Modals
import PointsTipModal from "../fans/PointsTipModal";
import AddToPlaylistModal from "../music/AddToPlaylistModal";
import CreatePlaylistModal from "../music/CreatePlaylistModal";
import ComingSoonModal from "../miscellaneous/ComingSoonModal";
import ProfileLink from "../profile/ProfileLink";
import ShoutoutModal from "../radio/ShoutoutModal";
import DailyCapModal from "../trends/DailyCapModal";
import usePressAndHold from "../../hooks/usePressAndHold";
import ReactionSelector from "../music/ReactionSelector";
import ReportTrackModal from "../miscellaneous/ReportTrackModal";

// Brand Assets
import atomicRadioHeader from "../../assets/atomicRadioHeader.png";
import likeIcon from "../../assets/like.png";
import likedIcon from "../../assets/trends_liked.png";
import dislikedIcon from "../../assets/trends_disliked.png";
import tipIcon from "../../assets/tip_icon.png";
import saveIcon from "../../assets/save.png";
import shareIcon from "../../assets/share.png";
import subscribeIcon from "../../assets/subscribe_grey.png";
import subscribedIcon from "../../assets/subscribe_colour.png";
import siteIcon from "../../assets/site_icon.png";

import likeWhite from "../../assets/trends_like.png";
import subscribeWhite from "../../assets/subscribe_white.png";
import tipWhite from "../../assets/tip_white.png";
import saveWhite from "../../assets/trends_save.png";
import shareWhite from "../../assets/trends_share.png";

// Reusable Marquee
const MarqueeText = ({ children, className = "" }) => {
  const containerRef = useRef(null);
  const textRef = useRef(null);
  const [overflowDistance, setOverflowDistance] = useState(0);

  useEffect(() => {
    const calculateOverflow = () => {
      if (containerRef.current && textRef.current) {
        const containerWidth = containerRef.current.clientWidth;
        const textWidth = textRef.current.scrollWidth;

        if (textWidth > containerWidth) {
          setOverflowDistance(textWidth - containerWidth + 12);
        } else {
          setOverflowDistance(0);
        }
      }
    };

    calculateOverflow();
    window.addEventListener("resize", calculateOverflow);
    return () => window.removeEventListener("resize", calculateOverflow);
  }, [children]);

  const duration = Math.max(4, overflowDistance / 35);

  return (
    <div ref={containerRef} className="w-full overflow-hidden whitespace-nowrap relative">
      <motion.div
        ref={textRef}
        className={`inline-block whitespace-nowrap ${className}`}
        animate={
          overflowDistance > 0
            ? { x: [0, 0, -overflowDistance, -overflowDistance, 0] }
            : { x: 0 }
        }
        transition={
          overflowDistance > 0
            ? {
                duration: duration + 4,
                times: [0, 0.2, 0.7, 0.9, 1],
                repeat: Infinity,
                ease: "easeInOut",
              }
            : {}
        }
      >
        {children}
      </motion.div>
    </div>
  );
};

const HomeRadioPlayer = ({ isRadioExpanded }) => {
  const { currentSong, isMuted, toggleMute, setMuteExplicit, playSource, tuneInToRadio, getRadioSyncData } = useMusic();
  const { user, activeProfile, isDarkMode } = useProfile();
  const [liveData, setLiveData] = useState(null);
  const [artistLocation, setArtistLocation] = useState("Atomic Hub");
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [showMuteOverlay, setShowMuteOverlay] = useState(false);

  // Modals state
  const [isPointsTipModalOpen, setIsPointsTipModalOpen] = useState(false);
  const [isAddToPlaylistOpen, setIsAddToPlaylistOpen] = useState(false);
  const [isCreatePlaylistOpen, setIsCreatePlaylistOpen] = useState(false);
  const [isComingSoonModalOpen, setIsComingSoonModalOpen] = useState(false);
  const [isShoutoutOpen, setIsShoutoutOpen] = useState(false);
  const [isCapModalOpen, setIsCapModalOpen] = useState(false);
  const [isReactionOpen, setIsReactionOpen] = useState(false);
  
  // Bio state & fallback
  const [artistBio, setArtistBio] = useState(null);
  const [isBioExpanded, setIsBioExpanded] = useState(false);
  const [hasBioOverflow, setHasBioOverflow] = useState(false);
  const bioRef = useRef(null);

  const menuRef = useRef(null);
  const closeTimerRef = useRef(null);
  const db = getFirestore();
  const navigate = useNavigate();

  const isJoined = playSource === "Radio";

  // Radio sync data
  useEffect(() => {
    const unsubscribe = onSnapshot(doc(db, "radio", "main"), (docSnap) => {
      if (docSnap.exists()) {
        const sync = getRadioSyncData(docSnap.data());
        setLiveData(sync);
      }
    });
    return () => unsubscribe();
  }, [db, getRadioSyncData]);

  const activeTrack = isJoined ? currentSong : liveData?.track;

  // Fetch Artist Location
  useEffect(() => {
    if (!activeTrack?.artistId) {
      setArtistLocation("Atomic Hub");
      return;
    }
    const artistDocRef = doc(db, "artist_accounts", activeTrack.artistId);
    const unsubscribe = onSnapshot(
      artistDocRef,
      (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          const city = data.city?.trim() || "";
          const state = (data.state || data.stateRegion)?.trim() || "";

          if (city && state) {
            setArtistLocation(`${city}, ${state}`);
          } else if (city) {
            setArtistLocation(city);
          } else if (state) {
            setArtistLocation(state);
          } else {
            setArtistLocation("Atomic Hub");
          }
        } else {
          setArtistLocation("Atomic Hub");
        }
      },
      () => setArtistLocation("Atomic Hub")
    );
    return () => unsubscribe();
  }, [activeTrack?.artistId, db]);

  // Fetch Artist Bio Fallback if activeTrack has no description
  useEffect(() => {
    setIsBioExpanded(false);
    let isMounted = true;

    const fetchArtistBio = async () => {
      if (activeTrack?.description?.trim() || !activeTrack?.artistId) {
        setArtistBio(null);
        return;
      }

      try {
        const artistDoc = await getDoc(doc(db, "artist_accounts", activeTrack.artistId));
        if (isMounted && artistDoc.exists()) {
          setArtistBio(artistDoc.data()?.bio?.trim() || null);
        }
      } catch (err) {
        console.error("Error fetching fallback radio artist bio:", err);
      }
    };

    fetchArtistBio();
    return () => {
      isMounted = false;
    };
  }, [activeTrack?.id, activeTrack?.artistId, activeTrack?.description, db]);

  // Priority: 1) Song Description -> 2) Artist Bio -> 3) None
  const displayBio = activeTrack?.description?.trim() || artistBio || null;

  // Check if text exceeds 2 lines
  useLayoutEffect(() => {
    if (!bioRef.current || !displayBio) {
      setHasBioOverflow(false);
      return;
    }
    if (!isBioExpanded) {
      const el = bioRef.current;
      setHasBioOverflow(el.scrollHeight > el.clientHeight);
    }
  }, [displayBio, isBioExpanded]);

  const {
    rating,
    rateSong,
    isSubscribed,
    toggleSubscription,
  } = useSongActions({
    songId: activeTrack?.id,
    artistId: activeTrack?.artistId,
  });

  const cancelCloseTimer = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const startCloseTimer = () => {
    cancelCloseTimer();
    closeTimerRef.current = setTimeout(() => {
      setIsReactionOpen(false);
    }, 300);
  };

  const handleRateToggle = async () => {
    if (!activeTrack) return;
    const newRating = rating === "liked" ? "disliked" : "liked";
    const result = await rateSong(newRating, "radio");
    if (result?.showCapModal) setIsCapModalOpen(true);
  };

  const handleExplicitReaction = async (selectedRating) => {
    if (!activeTrack) return;
    const result = await rateSong(selectedRating, "radio_reaction");
    if (result?.showCapModal) setIsCapModalOpen(true);
  };

  const ratingGestureHandlers = usePressAndHold(
    () => {
      cancelCloseTimer();
      setIsReactionOpen(true);
    },
    handleRateToggle,
    250
  );

  const handleInteraction = () => {
    if (!activeTrack) return;
    if (!isJoined) {
      setMuteExplicit(false);
      tuneInToRadio();
    } else {
      const becomingUnmuted = isMuted;
      toggleMute();
      if (becomingUnmuted) {
        setShowMuteOverlay(true);
        setTimeout(() => setShowMuteOverlay(false), 800);
      }
    }
  };

  const handleTipClick = () => {
    if (!user || user.isAnonymous || activeProfile?.isGuest) {
      sessionStorage.setItem("postAuthRedirect", window.location.pathname);
      toast("Please sign up to tip points to this artist!");
      navigate("/login");
      return;
    }
    if (activeProfile?.type === "artist") {
      toast.error("Artist accounts cannot boost songs.");
      return;
    }
    if (!activeTrack) {
      toast.error("No active song available to boost.");
      return;
    }
    setIsPointsTipModalOpen(true);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="w-full">
      <style>{`
        @keyframes marquee {
          0% { transform: translateX(0%); }
          15% { transform: translateX(0%); }
          85% { transform: translateX(-50%); }
          100% { transform: translateX(-50%); }
        }
      `}</style>

      <div
        className={`w-full rounded-2xl border p-4 sm:p-5 transition-all duration-300 shadow-xl ${
          isDarkMode
            ? "bg-zinc-950/80 border-zinc-800 text-white"
            : "bg-white border-slate-200 text-slate-900 shadow-slate-100"
        }`}
      >
        <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
          
          {/* ALBUM ART */}
          <div
            onClick={handleInteraction}
            className="relative w-full max-w-[280px] sm:max-w-[320px] md:w-60 md:h-60 aspect-square flex-shrink-0 cursor-pointer rounded-2xl overflow-hidden bg-black shadow-lg group"
          >
            {activeTrack ? (
              <>
                <img
                  src={activeTrack.albumArtworkURL}
                  alt={activeTrack.title || "Radio Art"}
                  className={`w-full h-full object-cover transition-all duration-700 ${
                    !isJoined ? "grayscale brightness-50" : "grayscale-0 group-hover:scale-105"
                  }`}
                />

                <div className="absolute top-0 left-0 w-full h-20 bg-gradient-to-b from-black/80 to-transparent pointer-events-none" />

                <div className="absolute top-3 right-3 z-10 flex items-center gap-1.5 bg-black/60 backdrop-blur-md px-2.5 py-1 rounded-full border border-white/10">
                  <div className="relative flex h-2 w-2">
                    {isJoined && (
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#ff00cc] opacity-75" />
                    )}
                    <span
                      className={`relative inline-flex rounded-full h-2 w-2 ${
                        isJoined ? "bg-[#ff00cc]" : "bg-white/50"
                      }`}
                    />
                  </div>
                  <span className="text-[9px] font-black uppercase tracking-widest text-white">
                    {isJoined ? "Live Broadcast" : "Radio Preview"}
                  </span>
                </div>

                {!isJoined && (
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <span className="bg-white/95 text-black font-black px-4 py-2 rounded-full shadow-2xl uppercase tracking-tighter text-xs">
                      Tap to Tune In
                    </span>
                  </div>
                )}

                <AnimatePresence>
                  {isJoined && isMuted && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="absolute inset-0 flex flex-col items-center justify-center bg-black/50 pointer-events-none"
                    >
                      <div className="bg-white/10 backdrop-blur-md border border-white/20 p-4 rounded-full shadow-2xl mb-2">
                        <FaVolumeMute className="text-2xl text-white" />
                      </div>
                      <span className="text-[9px] font-black uppercase tracking-widest text-white/80">
                        Audio Muted
                      </span>
                    </motion.div>
                  )}
                  {showMuteOverlay && !isMuted && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.5 }}
                      animate={{ opacity: 1, scale: 1.1 }}
                      exit={{ opacity: 0, scale: 1.3 }}
                      className="absolute inset-0 flex items-center justify-center pointer-events-none z-50"
                    >
                      <div className="bg-[#ff00cc] p-4 rounded-full shadow-[0_0_35px_rgba(255,0,204,0.6)]">
                        <FaVolumeUp className="text-2xl text-white" />
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </>
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <div className="w-8 h-8 border-2 border-brand-pink border-t-transparent rounded-full animate-spin" />
              </div>
            )}
          </div>

          {/* METADATA & CONTROLS */}
          <div className="flex-1 w-full min-w-0 flex flex-col justify-between text-left relative">
            <div>
              {/* Centered Atomic Radio Header & Options */}
              <div className="relative flex items-center justify-between mb-3 sm:mb-4 min-h-[44px]" ref={menuRef}>
                {isRadioExpanded && (
                  <div className="absolute inset-x-0 flex items-center justify-center pointer-events-none">
                    <motion.img
                      src={atomicRadioHeader}
                      alt="Atomic Radio"
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.95 }}
                      transition={{ duration: 0.3, ease: "easeOut" }}
                      className="h-10 sm:h-12 md:h-14 w-auto object-contain select-none"
                    />
                  </div>
                )}

                <div className="relative ml-auto z-10">
                  <button
                    onClick={() => setIsMenuOpen(!isMenuOpen)}
                    className={`p-1.5 rounded-full transition-colors focus:outline-none ${
                      isDarkMode
                        ? "text-zinc-400 hover:text-brand-pink"
                        : "text-slate-600 hover:text-brand-pink"
                    }`}
                    title="Options"
                  >
                    <span className="text-sm font-bold tracking-widest">•••</span>
                  </button>

                  {isMenuOpen && (
                    <div
                      className={`absolute right-0 top-8 z-50 w-44 rounded-xl border shadow-2xl py-1 text-left ${
                        isDarkMode
                          ? "bg-zinc-950 border-zinc-800 text-white"
                          : "bg-white border-slate-200 text-slate-900"
                      }`}
                    >
                      <button
                        onClick={() => {
                          setIsMenuOpen(false);
                          setIsReportModalOpen(true);
                        }}
                        className="w-full text-left px-4 py-2 text-xs font-semibold hover:bg-red-500/10 hover:text-red-500 transition-colors"
                      >
                        Report Track
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Title & Artist */}
              <div className="flex items-center gap-3.5 mt-1 overflow-hidden">
                <ProfileLink
                  profileId={activeTrack?.artistId || null}
                  photoURL={activeTrack?.artistPhotoURL || activeTrack?.resolvedArtworkUrl || siteIcon}
                  altText={activeTrack?.artistName || "Atomic Radio"}
                  size="w-12 h-12 flex-shrink-0"
                  className={!activeTrack?.artistId ? "cursor-default" : "cursor-pointer"}
                  source="radio_player"
                />

                <div className="min-w-0 flex-1 overflow-hidden">
                  <MarqueeText speedSeconds={14}>
                    <h3
                      className={`text-xl md:text-2xl font-black italic tracking-tight leading-tight ${
                        isDarkMode ? "text-white" : "text-slate-900"
                      }`}
                    >
                      {activeTrack?.title || "Atomic Radio Live"}
                    </h3>
                  </MarqueeText>

                  <MarqueeText speedSeconds={16} className="mt-0.5">
                    <p
                      className={`text-xs ${
                        isDarkMode ? "text-zinc-400" : "text-slate-500"
                      }`}
                    >
                      by{" "}
                      <span
                        className={`font-bold ${
                          isDarkMode ? "text-zinc-200" : "text-slate-900"
                        }`}
                      >
                        {activeTrack?.artistName || "Continuous Stream"}
                      </span>
                      {artistLocation && artistLocation !== "Atomic Hub" && (
                        <span
                          className={`font-semibold ${
                            isDarkMode ? "text-zinc-400" : "text-slate-600"
                          }`}
                        >
                          {" • "}{artistLocation}
                        </span>
                      )}
                    </p>
                  </MarqueeText>
                </div>
              </div>

              {/* Bio Section: Song Description -> Artist Bio -> None */}
              {displayBio && (
                <div className="mt-3 text-xs leading-relaxed text-left">
                  <p
                    ref={bioRef}
                    className={`transition-all duration-200 ${
                      isBioExpanded ? "max-h-40 overflow-y-auto pr-1 no-scrollbar" : "line-clamp-2"
                    } ${isDarkMode ? "text-zinc-400" : "text-slate-600"}`}
                  >
                    {displayBio}
                  </p>

                  {(hasBioOverflow || isBioExpanded) && (
                    <button
                      type="button"
                      onClick={() => setIsBioExpanded(!isBioExpanded)}
                      className="mt-1 text-[11px] font-bold text-brand-pink hover:underline focus:outline-none transition-colors block"
                    >
                      {isBioExpanded ? "Show less" : "Read more"}
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Actions */}
            <div
              className={`flex items-center justify-around sm:justify-start gap-4 sm:gap-6 mt-6 pt-4 border-t ${
                isDarkMode ? "border-zinc-800/80" : "border-slate-200"
              }`}
            >
              <div
                className="relative flex items-center justify-center"
                onMouseEnter={cancelCloseTimer}
                onMouseLeave={startCloseTimer}
              >
                <ReactionSelector
                  isOpen={isReactionOpen}
                  onClose={() => setIsReactionOpen(false)}
                  onSelectReaction={handleExplicitReaction}
                  onMouseEnter={cancelCloseTimer}
                  isDarkMode={isDarkMode}
                  position="top"
                />
                <button
                  {...ratingGestureHandlers}
                  className="p-2 transition-transform active:scale-90 hover:opacity-80"
                  title="Like / Reaction"
                >
                  <img
                    src={
                      rating === "liked"
                        ? likedIcon
                        : rating === "disliked"
                        ? dislikedIcon
                        : isDarkMode
                        ? likeWhite
                        : likeIcon
                    }
                    className="w-5 h-5 object-contain"
                    alt="Like"
                  />
                </button>
              </div>

              <button
                onClick={() =>
                  toggleSubscription(activeTrack?.artistId, activeTrack?.artistName, "radio")
                }
                className="p-2 transition-transform active:scale-90 hover:opacity-80"
                title="Follow Artist"
              >
                <img
                  src={
                    isSubscribed
                      ? subscribedIcon
                      : isDarkMode
                      ? subscribeWhite
                      : subscribeIcon
                  }
                  className="w-5 h-5 object-contain"
                  alt="Subscribe"
                />
              </button>

              <button
                onClick={handleTipClick}
                className="p-2 transition-transform active:scale-90 hover:opacity-80"
                title="Boost Song"
              >
                <img
                  src={isDarkMode ? tipWhite : tipIcon}
                  className="w-5 h-5 object-contain"
                  alt="Tip"
                />
              </button>

              <button
                onClick={() => setIsAddToPlaylistOpen(true)}
                className="p-2 transition-transform active:scale-90 hover:opacity-80"
                title="Save to Playlist"
              >
                <img
                  src={isDarkMode ? saveWhite : saveIcon}
                  className="w-5 h-5 object-contain"
                  alt="Save"
                />
              </button>

              <button
                onClick={() => setIsComingSoonModalOpen(true)}
                className="p-2 transition-transform active:scale-90 hover:opacity-80"
                title="Share"
              >
                <img
                  src={isDarkMode ? shareWhite : shareIcon}
                  className="w-5 h-5 object-contain"
                  alt="Share"
                />
              </button>
            </div>
          </div>
        </div>
      </div>

      <ReportTrackModal
        song={activeTrack}
        isOpen={isReportModalOpen}
        onClose={() => setIsReportModalOpen(false)}
        isDarkMode={isDarkMode}
      />
      <AddToPlaylistModal
        isOpen={isAddToPlaylistOpen}
        onClose={() => setIsAddToPlaylistOpen(false)}
        songToAdd={activeTrack}
        onNavigateToCreate={() => {
          setIsAddToPlaylistOpen(false);
          setIsCreatePlaylistOpen(true);
        }}
      />
      <CreatePlaylistModal
        isOpen={isCreatePlaylistOpen}
        onClose={() => setIsCreatePlaylistOpen(false)}
        songToAdd={activeTrack}
      />
      <PointsTipModal
        isOpen={isPointsTipModalOpen}
        onClose={() => setIsPointsTipModalOpen(false)}
        songToTip={activeTrack}
        isDarkMode={isDarkMode}
      />
      <ComingSoonModal
        isOpen={isComingSoonModalOpen}
        onClose={() => setIsComingSoonModalOpen(false)}
      />
      <ShoutoutModal
        isOpen={isShoutoutOpen}
        onClose={() => setIsShoutoutOpen(false)}
        programName={activeTrack?.programName || "Standard"}
        folderId={activeTrack?.folderId}
      />
      <DailyCapModal
        isOpen={isCapModalOpen}
        onClose={() => setIsCapModalOpen(false)}
        isDarkMode={isDarkMode}
      />
    </div>
  );
};

export default HomeRadioPlayer;